import { Head, usePage } from '@inertiajs/react'
import { useState, useRef, useEffect } from 'react'
import type { FormEvent, ChangeEvent, ReactNode } from 'react'
import Layout from '@/layouts/Layout'
import Button from '@/components/ui/Button'

interface Message {
  id: string
  text: string
  isUser: boolean
  isImage?: boolean
  timestamp: Date
}

interface PageProps {
  csrfToken: string
  [key: string]: unknown
}

export default function Chat() {
  const { csrfToken } = usePage<PageProps>().props
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'system-welcome',
      text: "Welcome! You can test all the same commands you'd use via SMS here.",
      isUser: false,
      timestamp: new Date(),
    },
  ])
  const [inputValue, setInputValue] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isReminderLoading, setIsReminderLoading] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textInputRef = useRef<HTMLInputElement>(null)

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount
  useEffect(() => {
    textInputRef.current?.focus()
  }, [])

  const addMessage = (text: string, isUser: boolean, isImage = false) => {
    const newMessage: Message = {
      id: `msg-${Date.now()}-${Math.random()}`,
      text,
      isUser,
      isImage,
      timestamp: new Date(),
    }
    setMessages((prev) => [...prev, newMessage])
  }

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const clearFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    const message = inputValue.trim()
    const file = selectedFile

    if (!message && !file) return

    // Add user message/photo preview
    if (file) {
      const reader = new FileReader()
      reader.onload = (evt) => {
        if (evt.target?.result) {
          addMessage(evt.target.result as string, true, true)
        }
      }
      reader.readAsDataURL(file)
    }

    if (message) {
      addMessage(message, true)
    }

    setInputValue('')
    setIsLoading(true)

    try {
      const formData = new FormData()
      if (message) {
        formData.append('message', message)
      }
      if (file) {
        formData.append('attachment', file)
      }

      const response = await fetch('/messages/chat', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      })

      const data = await response.json()

      if (data.success && data.messages) {
        data.messages.forEach((msg: string) => {
          if (msg && msg.trim()) {
            addMessage(msg, false)
          }
        })
      } else {
        addMessage('Sorry, there was an error processing your message.', false)
      }
    } catch (error) {
      console.error('Error:', error)
      addMessage('Sorry, there was an error connecting to the server.', false)
    } finally {
      setIsLoading(false)
      clearFile()
      textInputRef.current?.focus()
    }
  }

  const handleTestReminder = async () => {
    setIsReminderLoading(true)
    try {
      const response = await fetch('/messages/chat/reminder', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      })

      const data = await response.json()
      if (data.success && Array.isArray(data.messages)) {
        data.messages.forEach((msg: string) => {
          if (msg && msg.trim()) {
            addMessage(msg, false)
          }
        })
      } else {
        addMessage('Unable to generate a reminder right now.', false)
      }
    } catch (error) {
      console.error('Reminder error:', error)
      addMessage('Sorry, there was an error generating the reminder.', false)
    } finally {
      setIsReminderLoading(false)
    }
  }

  return (
    <>
      <Head title="Chat" />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold mb-2">Chat with 3pm</h1>
          <p className="text-gray-600">Test the SMS interaction system through the web interface</p>
        </div>

        {/* Chat Container */}
        <div className="border border-gray-200 rounded-lg h-[500px] flex flex-col bg-gray-50">
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`max-w-[70%] rounded-lg p-3 ${
                  msg.isUser
                    ? 'bg-blue-500 text-white self-end'
                    : 'bg-white border border-gray-200 self-start'
                }`}
              >
                {msg.isImage && msg.isUser ? (
                  <img src={msg.text} alt="Uploaded" className="max-w-full rounded" />
                ) : (
                  <span>{msg.text}</span>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
            <form onSubmit={handleSubmit} className="flex flex-col gap-2">
              <div className="flex gap-2 items-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-100 border border-gray-200 rounded hover:bg-gray-200 disabled:opacity-50"
                  title="Upload photo"
                >
                  📷
                </button>
                <input
                  type="text"
                  ref={textInputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type a message..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50"
                  autoComplete="off"
                />
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send'}
                </Button>
                <button
                  type="button"
                  onClick={handleTestReminder}
                  disabled={isReminderLoading}
                  className="px-4 py-2 bg-yellow-50 text-yellow-800 border border-yellow-300 rounded font-semibold hover:bg-yellow-100 disabled:opacity-50"
                >
                  {isReminderLoading ? 'Generating...' : 'Test Reminder'}
                </button>
              </div>

              {/* File Preview */}
              {selectedFile && (
                <div className="flex items-center gap-2 p-2 bg-gray-100 rounded text-sm text-gray-600">
                  <span>{selectedFile.name}</span>
                  <button
                    type="button"
                    onClick={clearFile}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-4 text-sm text-gray-500">
          <p>
            <strong>Try commands like:</strong> "follow @username", "who am I following?", "what's my
            profile link?", "update my location to San Francisco", etc.
          </p>
          <p className="mt-1">
            <strong>Upload photos:</strong> Click the 📷 button to upload JPEG/HEIC/PNG photos
            (they'll be posted automatically) or GIF for snarky responses!
          </p>
        </div>
      </div>
    </>
  )
}

Chat.layout = (page: ReactNode) => <Layout>{page}</Layout>
